import { logger } from 'config/logger';
import { isAxiosError } from 'helpers/clientHelpers';
import { Dictionary } from 'shared/legacy/contexts/LocalizationContext/types';

type ApiErrorResponse = {
  status: string;
  message: string;
  details?: string;
};

export const parseJobImprovedErrorMessage = (error: unknown, dictionary: Dictionary): string => {
  try {
    // Handle axios errors from API
    if (isAxiosError<ApiErrorResponse>(error)) {
      const responseData = error.response?.data;

      if (responseData?.message === 'Failed to save processed vacancy.' && responseData?.details) {
        const fieldPaths = extractMissingFieldPaths(responseData.details);
        if (fieldPaths.length > 0) {
          const fieldLabels = fieldPaths.map((path) => getJobImprovedFieldLabel(path, dictionary));
          return dictionary.failedToProcessAtsVacancyMissing({ missingFields: fieldLabels.join(', ') });
        }
      }

      // Fallback to generic error
      return responseData?.message || error.message || dictionary.errorProcessingAtsVacancy;
    }

    // Handle standard Error objects
    if (error instanceof Error) {
      return error.message || dictionary.somethingWentWrong;
    }

    return dictionary.somethingWentWrong;
  } catch (parseError) {
    logger.error(parseError);
    return dictionary.errorProcessingAtsVacancy;
  }
};

export const getJobImprovedFieldLabel = (fieldPath: string, dictionary: Dictionary): string => {
  const fieldMap: Record<string, string> = {
    'job_details.job_title': dictionary.jobTitle,
    'job_details.employment_type': dictionary.employmentType,
    'job_details.salary_range': dictionary.salary,
    'job_details.salary_unit': dictionary.salaryUnit,
    'job_details.application_deadline': dictionary.applicationDeadline,
    'employment_requirements.address': dictionary.workAddress,
    'employment_requirements.radius_km': dictionary.radiusKm,
    'employment_requirements.working_hours_range': dictionary.workingHoursRange,
    'employment_requirements.shifts': dictionary.shifts,
    'employment_requirements.workdays': dictionary.workdays,
    'position_overview.description': dictionary.positionOverview,
    'requirements.minimum_education_level': dictionary.minimumEducationLevel,
    'requirements.drivers_license': dictionary.driversLicense,
    'requirements.maximum_language_skills': dictionary.languagesLabel,
    'requirements.certificates': dictionary.certification,
    'responsibilities.key_responsibilities_tasks': dictionary.keyTasks,
    'compensation_benefits.notes': dictionary.secondaryBenefits,
  };

  return fieldMap[fieldPath] || fieldPath.split('.').pop()?.replace(/_/g, ' ') || fieldPath;
};

export const extractMissingFieldPaths = (errorDetails: string): string[] => {
  try {
    const cleanedDetails = errorDetails.replace(/'/g, '"').replace(/ErrorDetail\([^)]*\)/g, '""');
    const details = JSON.parse(cleanedDetails);
    const paths: string[] = [];

    const traverse = (obj: any, currentPath: string[] = []) => {
      Object.keys(obj).forEach((key) => {
        const newPath = [...currentPath, key];
        const value = obj[key];

        if (Array.isArray(value) && value.length > 0) {
          paths.push(newPath.join('.'));
        } else if (value && typeof value === 'object') {
          traverse(value, newPath);
        }
      });
    };

    traverse(details);
    return paths;
  } catch (error) {
    return [];
  }
};
